import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  Paper,
  Divider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Pagination
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  History as VersionIcon,
  Computer as InDesignIcon,
  Description as PowerPointIcon,
  Label as KeywordIcon,
  Star as FavoriteIcon,
  Category as CategoryIcon,
  LocalOffer as BadgeIcon,
  Person as PersonIcon,
  VpnKey as ApiKeyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';

interface AssetActivity {
  _id: string;
  assetId: string;
  activityType: string;
  timestamp: string;
  details: {
    fileName?: string;
    fileSize?: number;
    downloadFormat?: string;
    fieldChanged?: string;
    previousValue?: any;
    newValue?: any;
    clientApp?: string;
    ipAddress?: string;
    userAgent?: string;
    versionNote?: string;
  };
  userId?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  apiKeyId?: {
    _id: string;
    name: string;
  };
}

interface AssetHistoryProps {
  assetId: string;
}

const AssetHistory: React.FC<AssetHistoryProps> = ({ assetId }) => {
  const [activities, setActivities] = useState<AssetActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const pageSize = 20;

  // Activity type display configurations
  const activityConfig = {
    upload: { icon: UploadIcon, color: '#4caf50', label: 'Uppladdad' },
    download: { icon: DownloadIcon, color: '#2196f3', label: 'Nedladdad' },
    // view intentionally hidden from filter (no longer logged)
    view: { icon: ViewIcon, color: '#9e9e9e', label: 'Visad' },
    edit_metadata: { icon: EditIcon, color: '#ff9800', label: 'Metadata ändrad' },
    version_upload: { icon: VersionIcon, color: '#9c27b0', label: 'Ny version' },
    indesign_usage: { icon: InDesignIcon, color: '#e91e63', label: 'Använd i InDesign' },
    powerpoint_usage: { icon: PowerPointIcon, color: '#3f51b5', label: 'Använd i PowerPoint' },
    keyword_added: { icon: KeywordIcon, color: '#607d8b', label: 'Nyckelord tillagt' },
    favorite_added: { icon: FavoriteIcon, color: '#ffc107', label: 'Markerad som favorit' },
    category_changed: { icon: CategoryIcon, color: '#795548', label: 'Kategori ändrad' },
    badge_added: { icon: BadgeIcon, color: '#009688', label: 'Badge tillagt' },
    badge_removed: { icon: BadgeIcon, color: '#f44336', label: 'Badge borttaget' },
    validoo_sync: { icon: RefreshIcon, color: '#00695c', label: 'Synkad med Validoo' }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize
      };

      if (filterType !== 'all') {
        params.activityType = filterType;
      }

      const response = await axiosInstance.get(`/api/activities/asset/${assetId}`, { params });
      
      setActivities(response.data.activities);
      setTotalCount(response.data.pagination.total);
      setTotalPages(Math.ceil(response.data.pagination.total / pageSize));
    } catch (err: any) {
      console.error('Error fetching asset activities:', err);
      setError('Kunde inte ladda aktivitetshistorik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assetId) {
      fetchActivities();
    }
  }, [assetId, page, filterType]);

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserDisplayName = (activity: AssetActivity) => {
    if (activity.userId) {
      const { firstName, lastName, email } = activity.userId;
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      return email;
    }
    if (activity.apiKeyId) {
      return activity.apiKeyId.name;
    }
    return 'Okänd användare';
  };

  const getUserAvatar = (activity: AssetActivity) => {
    if (activity.userId) {
      const name = getUserDisplayName(activity);
      return name.charAt(0).toUpperCase();
    }
    return 'K'; // K för API Key
  };

  // Helpers to format differences
  const formatValueAsText = (v: any): string => {
    if (Array.isArray(v)) return v.map(String).join(', ');
    if (v === null || v === undefined) return '';
    return String(v);
  };

  const renderBoldDiff = (prev: any, next: any) => {
    // Arrays: bold only items newly introduced in next
    if (Array.isArray(next)) {
      const prevSet = new Set((Array.isArray(prev) ? prev : []).map((x: any) => String(x)));
      return (
        <>
          {next.map((item: any, idx: number) => {
            const text = String(item);
            const isNew = !prevSet.has(text);
            return (
              <Box key={idx} component="span" sx={{ fontWeight: isNew ? 700 : 'inherit' }}>
                {idx > 0 ? ', ' : ''}{text}
              </Box>
            );
          })}
        </>
      );
    }

    // Strings/numbers: bold tokens that are new in next compared to prev
    const prevTokens = new Set(formatValueAsText(prev).split(/\s+/).filter(Boolean));
    const nextTokens = formatValueAsText(next).split(/\s+/);
    return (
      <>
        {nextTokens.map((tok, i) => {
          const key = `${tok}-${i}`;
          const isNew = tok && !prevTokens.has(tok);
          return (
            <Box key={key} component="span" sx={{ fontWeight: isNew ? 700 : 'inherit' }}>
              {i > 0 ? ' ' : ''}{tok}
            </Box>
          );
        })}
      </>
    );
  };

  const getActivityDescription = (activity: AssetActivity): React.ReactNode => {
    const config = activityConfig[activity.activityType as keyof typeof activityConfig];
    const details = activity.details;

    switch (activity.activityType) {
      case 'upload':
        return `${config.label} - ${details.fileName} (${Math.round((details.fileSize || 0) / 1024 / 1024 * 100) / 100} MB)`;
      case 'download':
        const format = details.downloadFormat === 'original' ? 'original' : details.downloadFormat;
        return `${config.label} - ${format} format`;
      case 'edit_metadata':
        return (
          <>
            {config.label} - {details.fieldChanged}: "{formatValueAsText(details.previousValue)}" → "{renderBoldDiff(details.previousValue, details.newValue)}"
          </>
        );
      case 'version_upload':
        return `${config.label}${details.versionNote ? ` - ${details.versionNote}` : ''}`;
      case 'indesign_usage':
      case 'powerpoint_usage':
        return `${config.label} - ${details.clientApp || 'Desktop app'}`;
      default:
        return config.label;
    }
  };

  const getActivityIcon = (activity: AssetActivity) => {
    const config = activityConfig[activity.activityType as keyof typeof activityConfig];
    if (!config) return ViewIcon;
    return config.icon;
  };

  const getActivityColor = (activity: AssetActivity) => {
    const config = activityConfig[activity.activityType as keyof typeof activityConfig];
    if (!config) return '#9e9e9e';
    return config.color;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filter and Stats */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Aktivitetstyp</InputLabel>
          <Select
            value={filterType}
            label="Aktivitetstyp"
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="all">Alla aktiviteter</MenuItem>
            <MenuItem value="upload">Uppladdningar</MenuItem>
            <MenuItem value="download">Nedladdningar</MenuItem>
            {/* 'view' not included to reduce noise */}
            <MenuItem value="validoo_sync">Validoo-synk</MenuItem>
            <MenuItem value="edit_metadata">Metadata ändringar</MenuItem>
            <MenuItem value="version_upload">Nya versioner</MenuItem>
            <MenuItem value="indesign_usage">InDesign användning</MenuItem>
            <MenuItem value="powerpoint_usage">PowerPoint användning</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {totalCount} aktiviteter totalt
          </Typography>
          <IconButton size="small" onClick={fetchActivities} title="Uppdatera">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Activities List */}
      {activities.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Inga aktiviteter att visa
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ mb: 2 }}>
          <List dense>
            {activities.map((activity, index) => {
              const IconComponent = getActivityIcon(activity);
              const color = getActivityColor(activity);

              return (
                <React.Fragment key={activity._id}>
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: color, width: 32, height: 32 }}>
                        <IconComponent sx={{ fontSize: 18 }} />
                      </Avatar>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" component="span">
                            {getActivityDescription(activity)}
                          </Typography>
                          {activity.details.clientApp && (
                            <Chip 
                              label={activity.details.clientApp} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {activity.userId ? (
                              <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            ) : (
                              <ApiKeyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {getUserDisplayName(activity)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            •
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(activity.timestamp)}
                          </Typography>
                          {activity.details.ipAddress && (
                            <>
                              <Typography variant="caption" color="text.secondary">
                                •
                              </Typography>
                              <Tooltip title={`IP: ${activity.details.ipAddress}`}>
                                <Typography variant="caption" color="text.secondary">
                                  {activity.details.ipAddress}
                                </Typography>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < activities.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default AssetHistory;
